import { Alert, Box, LoadingButton, Stack, TextField, Typography } from '@montugroup/design-system';
import { styled } from '@mui/material';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import useUpdatePassword from '@/hooks/rest/useUpdatePassword';
import { Logger } from '@/utils/logger';

import { zodResolver } from '@hookform/resolvers/zod';
import { UserPasswordSchema } from '@montugroup/circuit-api-contracts';
import { ShowPasswordAdornment } from '@montugroup/circuit-shared';
import { PasswordRequirements } from '@montugroup/circuit-shared/components/common/PasswordRequirements';
import { z } from 'zod';
import type { IAlert } from './types';

enum PasswordErrorCode {
  PASSWORD_REUSE = 'PasswordReuseError',
  CURRENT_PASSWORD = 'CurrentPasswordError',
  REQUIREMENTS_NOT_MET = 'password.requirements_not_met',
}

const logger = new Logger('resetPassword');

const Form = styled(Box)(({ theme }) => ({
  margin: theme.spacing(6, 0),
}));

const FormItem = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: theme.spacing(4, 0),
  [theme.breakpoints.up('lg')]: {
    maxWidth: '33%',
    paddingRight: theme.spacing(3),
  },
}));

const AlertResponse = styled(Alert)<{ type: 'success' | 'error' }>(({ theme, type }) => ({
  backgroundColor: type === 'success' ? theme.palette.success.light : theme.palette.error.light,
  color: type === 'success' ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: theme.typography.fontWeightBold,
  margin: theme.spacing(4, 0),
  width: '100%',
  '& .MuiAlert-message': {
    padding: theme.spacing(2.5, 0),
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '375px',
  },
}));

export interface ResetPasswordFormData {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

const formSchema = z
  .object({
    currentPassword: z.string().min(1, { message: 'You must enter your current password' }),
    password: UserPasswordSchema,
    confirmPassword: z.string().min(1, { message: 'Please confirm your new password' }),
  })
  .superRefine((values, ctx) => {
    if (values.currentPassword === values.password) {
      ctx.addIssue({
        message: 'New password cannot be the same as current password',
        code: 'custom',
        path: ['password'],
      });
    }

    if (values.confirmPassword !== values.password) {
      ctx.addIssue({
        message: 'Passwords do not match',
        code: 'custom',
        path: ['confirmPassword'],
      });
    }
  });

export default function PasswordReset() {
  const { mutate, isPending } = useUpdatePassword();
  const [alert, setAlert] = useState<IAlert>({ severity: 'success', message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { control, handleSubmit, reset, trigger, formState } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
  });
  const { isValid } = formState;

  const handleAlertClose = () => {
    setAlert({ severity: 'success', message: '' });
  };

  const handlePasswordSubmit: SubmitHandler<ResetPasswordFormData> = async (formData: ResetPasswordFormData) => {
    mutate(formData, {
      onSuccess: () => {
        setAlert({ severity: 'success', message: 'Password updated' });
        reset({}, { keepValues: false });
      },
      onError: (error: any) => {
        if (error.status === 403) {
          switch (error?.response?.data?.error?.code) {
            case PasswordErrorCode.REQUIREMENTS_NOT_MET:
              setAlert({
                severity: 'error',
                message:
                  'The password you entered is invalid as it does not meet our security requirements. Please choose a different password.',
              });
              break;
            default:
              setAlert({
                severity: 'error',
                message: error?.response?.data?.error?.message || 'Invalid password. Please try again.',
              });
          }
        } else {
          switch (error.error_code) {
            case PasswordErrorCode.PASSWORD_REUSE:
              setAlert({
                severity: 'error',
                message: 'This password has been used before. Please choose a different password.',
              });
              break;
            case PasswordErrorCode.CURRENT_PASSWORD:
              setAlert({
                severity: 'error',
                message: 'This is your current password. Please choose a different password.',
              });
              break;
            default:
              setAlert({ severity: 'error', message: 'Something went wrong, please try again' });
          }
        }

        logger.error('Password details not updated', error);
      },
    });
  };

  return (
    <Box>
      <Stack spacing={6}>
        <Typography variant="subtitle1" component="h2" sx={(theme) => ({ color: theme.palette.primary.main })}>
          Password
        </Typography>
        <Typography mt={6} mb={6} variant="body2">
          To update your password, please enter your new password below. Your password must:
          <PasswordRequirements />
        </Typography>
        <Form component="form" role="form" data-private onSubmit={handleSubmit(handlePasswordSubmit)}>
          <Stack spacing={6}>
            <FormItem>
              <Controller
                name="currentPassword"
                control={control}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                  <TextField
                    type="password"
                    variant="outlined"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    onBlur={onBlur}
                    label="Current Password"
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormItem>
            <FormItem>
              <Controller
                name="password"
                control={control}
                render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                  <TextField
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    variant="outlined"
                    inputRef={ref}
                    onChange={onChange}
                    onBlur={async () => {
                      await trigger('password');
                    }}
                    fullWidth
                    label="New Password"
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <ShowPasswordAdornment
                          testId="toggle-new-password-visibility"
                          isRevealed={showPassword}
                          toggle={setShowPassword}
                        />
                      ),
                    }}
                  />
                )}
              />
            </FormItem>
            <FormItem>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                  <TextField
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={value}
                    variant="outlined"
                    inputRef={ref}
                    onChange={onChange}
                    onBlur={async () => {
                      await trigger('confirmPassword');
                    }}
                    fullWidth
                    label="Confirm Password"
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <ShowPasswordAdornment
                          testId="toggle-confirm-password-visibility"
                          isRevealed={showConfirmPassword}
                          toggle={setShowConfirmPassword}
                        />
                      ),
                    }}
                  />
                )}
              />
            </FormItem>
            <FormItem>
              <LoadingButton
                loading={isPending}
                id="btnSubmit"
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={!isValid}
              >
                Update Password
              </LoadingButton>
            </FormItem>

            {alert.message && (
              <AlertResponse severity={alert.severity} type={alert.severity} onClose={handleAlertClose}>
                {alert.message}
              </AlertResponse>
            )}
          </Stack>
        </Form>
      </Stack>
    </Box>
  );
}
